import React, {
  forwardRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  type ChangeEvent,
  type JSX,
} from 'react';
import { StringParser } from '@mentimeter/string-parser';
import type { SimpleInlineNodesT } from '@mentimeter/ragnar-markdown';
import { clsx } from '@mentimeter/ragnar-tailwind-config';
import { useTranslations } from 'use-intl';
import { Box } from '../../box';
import { ErrorMessage } from '../../error-message';
import {
  type ValidatableT,
  runValidators,
  VALIDATORS,
} from '../../utils/_Validators';
import { MarkdownMenu } from '../../utils/_MarkdownMenu';
import FocusableCharacterCount from '../../utils/_FocusableCharacterCount';
import { type TextInputItemT, TextInputItem } from '../text-input-item';
import { TextInputIcon } from './tw/components';

export interface TextInputT extends TextInputItemT, ValidatableT {
  maxLength?: number;
  defaultValue?: string | undefined;
  displayMarkdownMenu?: boolean | undefined;
  mdMenuOptions?: Array<SimpleInlineNodesT>;
  iconLeading?: JSX.Element | undefined | null;
  iconLeadingAction?: () => void;
  iconLeadingLabel?: string;
  iconTrailing?: JSX.Element;
  iconTrailingAction?: () => void;
  iconTrailingLabel?: string;
  trackMarkdown?: (type: string) => void;
}
export const TextInput = forwardRef<HTMLInputElement, TextInputT>(
  (
    {
      defaultValue,
      maxLength,
      onChange,
      placeholder,
      value = '',
      validators = [],
      displayMarkdownMenu = false,
      mdMenuOptions,
      onValidation,
      onFocus: _of,
      onBlur: _ob,
      iconLeading,
      iconTrailing,
      iconLeadingAction,
      iconTrailingAction,
      iconLeadingLabel,
      iconTrailingLabel,
      trackMarkdown,
      id,
      inputSize = 'default',
      'aria-describedby': ariaDescribedby,
      mb,
      mt,
      m,
      ml,
      mr,
      mx,
      my,
      className,
      p,
      py,
      pr,
      pl,
      pb,
      pt,
      px,
      width,
      height,
      ...rest
    },
    forwardedRef,
  ) => {
    const containerStyling = {
      mb,
      mt,
      m,
      ml,
      mr,
      mx,
      my,
      width,
      height,
      pb,
      pt,
      p,
      pl,
      pr,
      px,
      py,
    };

    const [currentValue, setCurrentValue] = useState(String(value));
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [elementId, setElementId] = useState<string>('');
    const [hardCapped, setHardCapped] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const t = useTranslations('ragnar.input');

    useEffect(() => {
      if (!id) {
        setElementId(`input-${Math.floor(Math.random() * 10000000000)}`);
      } else {
        setElementId(id);
      }
    }, [id]);

    // use forwardedRef if given, otherwise inputRef
    const reference =
      forwardedRef !== undefined && forwardedRef !== null
        ? forwardedRef
        : inputRef;

    // dynamically adjust maxLength so markdown "doesn't count"
    const stringParserRef = useRef<HTMLDivElement | null>(null);
    const [maxLengthAdjusted, setMaxLengthAdjusted] = useState(
      maxLength || null,
    );
    useEffect(() => {
      // maxLength should only need to be adjusted if md menu is enabled
      if (
        maxLength &&
        displayMarkdownMenu &&
        stringParserRef.current &&
        stringParserRef.current.innerText
      ) {
        const valueAfterParse = stringParserRef.current.innerText.trim();
        setMaxLengthAdjusted(
          maxLength + currentValue.length - valueAfterParse.length,
        );
      } else {
        setMaxLengthAdjusted(maxLength || null);
      }
    }, [stringParserRef, currentValue, maxLength, displayMarkdownMenu]);

    const defaultValidators = useMemo(
      () =>
        maxLengthAdjusted ? [VALIDATORS.maxLength(maxLengthAdjusted)] : [],
      [maxLengthAdjusted],
    );

    const validate = useCallback(
      (value: string) => {
        const allValidators = [...defaultValidators, ...validators];
        return runValidators(allValidators, (validators) => {
          setHardCapped(Boolean(validators['maxLength']));
          if (onValidation) onValidation(validators);
        })(value);
      },
      [onValidation, validators, defaultValidators],
    );

    useEffect(() => {
      setCurrentValue(String(value));
    }, [value]);

    useEffect(() => {
      validate(currentValue);
    }, [currentValue, validate]);

    // menti-style placeholders
    const handleFocus = useCallback(
      (focused: boolean) => {
        setIsFocused(focused);
        if (focused && defaultValue === value) {
          setCurrentValue('');
        } else {
          setCurrentValue(String(value));
        }
      },
      [setCurrentValue, defaultValue, value],
    );

    const handleUpdate = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setCurrentValue(newValue);
        if (onChange) onChange(event);
      },
      [onChange],
    );

    const inputItemClasses = clsx(
      iconLeading && 'pl-8',
      iconLeading && inputSize === 'default' && 'pl-10',
      (iconTrailing || maxLength) && 'pr-8',
      (iconTrailing || maxLength) && inputSize === 'default' && 'pr-10',
      iconTrailing && maxLength && 'pr-14',
      defaultValue === value ? 'text-text-weaker' : undefined,
    );

    return (
      <Box
        {...containerStyling}
        className={clsx('w-full', className)}
        data-testid="text-input-container"
      >
        <FocusableCharacterCount
          id={`${elementId}-character-counter`}
          iconTrailing={Boolean(iconTrailing)}
          onFocus={() => handleFocus(true)}
          onBlur={() => handleFocus(false)}
          maxLength={maxLengthAdjusted ?? undefined}
          value={currentValue}
          render={({ value, onBlur, onFocus }) => (
            <Box className="relative w-full">
              {iconLeading && (
                <TextInputIcon
                  icon={iconLeading}
                  iconLabel={iconLeadingLabel}
                  iconAction={iconLeadingAction}
                  inputSize={inputSize}
                  iconType="leading"
                />
              )}
              <TextInputItem // rest needs to be before onChange to not overwrite it
                {...rest}
                id={elementId}
                ref={reference}
                aria-describedby={`${
                  ariaDescribedby ? ariaDescribedby : ''
                } ${elementId}-character-counter max-length-${elementId}`}
                placeholder={defaultValue || placeholder}
                onFocus={(e) => {
                  onFocus(e);
                  if (_of) _of(e); // <Input />'s onFocus
                }}
                onBlur={(e) => {
                  onBlur(e);
                  if (_ob) _ob(e); // <Input />'s onBlur
                }}
                maxLength={maxLengthAdjusted ?? undefined}
                onChange={handleUpdate}
                value={value}
                inputSize={inputSize}
                className={inputItemClasses}
              />
              {iconTrailing && (
                <TextInputIcon
                  icon={iconTrailing}
                  iconLabel={iconTrailingLabel}
                  iconAction={iconTrailingAction}
                  inputSize={inputSize}
                  iconType="trailing"
                />
              )}

              {/*render parsed text to be able to get actual text length (not visible to user)*/}
              <Box ref={stringParserRef} className="hidden">
                <StringParser
                  source={value}
                  disableLineBreaks
                  allowHyperlinks
                />
              </Box>

              {displayMarkdownMenu && isFocused && (
                <MarkdownMenu
                  forwardedRef={reference}
                  options={mdMenuOptions}
                  text={value}
                  onUpdate={handleUpdate}
                  track={trackMarkdown}
                />
              )}
            </Box>
          )}
        />
        {isFocused && hardCapped && (
          <ErrorMessage className="mt-2" id={`max-length-${elementId}`}>
            {t('max_characters_message')}
          </ErrorMessage>
        )}
      </Box>
    );
  },
);
